import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Trophy, ShieldQuestion, Ruler, Weight, Activity, Film, PlayCircle, BarChart3, HeartPulse, Lock, History, ChevronRight, Loader2, Gauge, LineChart as LineChartIcon, Columns } from 'lucide-react';
import { fetchPublicPlayerProfile, friendlyError, buildAssetUrl, fetchPlayerIntelligenceLatest, fetchPlayerIntelligenceMatches, fetchPlayerIntelligenceConsolidated } from '@/lib/api';
import { VideoDownloadInline } from '@/components/downloads/VideoDownloadInline';
import { resolvePlayerAvatar } from '@/lib/playerAvatar';
import { PlayerIntelligencePerformance } from '@/components/players/PlayerIntelligencePerformance';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from 'recharts';

const SIDE_LABEL = { right: 'Derecho', left: 'Izquierdo', both: 'Ambidiestro' };

export default function PlayerPublicProfile() {
  const { clubSlug, teamSlug, playerSlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchPublicPlayerProfile(clubSlug, teamSlug, playerSlug)
      .then((d) => { if (alive) setData(d); })
      .catch((err) => { if (alive) setError(friendlyError(err)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [clubSlug, teamSlug, playerSlug]);

  // Fase 3A — carga del último análisis autorizado para el jugador
  // públicamente visible. Se dispara sólo cuando la ficha pública ha
  // resuelto (`data.id` es el `player_profile_id`). Si la API falla,
  // el componente muestra su estado vacío estándar (sin fallback).
  const [publicLatest, setPublicLatest] = useState(null);
  useEffect(() => {
    const pid = data?.id;
    if (!pid) { setPublicLatest(null); return undefined; }
    let alive = true;
    fetchPlayerIntelligenceLatest(pid)
      .then((res) => { if (alive) setPublicLatest(res || null); })
      .catch(() => { if (alive) setPublicLatest(null); });
    return () => { alive = false; };
  }, [data?.id]);
  const publicPlayerIntelligence = React.useMemo(() => {
    if (!publicLatest || !publicLatest.normalized_data) return { player_matches: [] };
    // Conservar el id real del player_match para que el CTA de análisis
    // completo navegue a /jugadores/:playerProfileId/analisis/:matchId.
    return { player_matches: [{ ...publicLatest.normalized_data, player_match_id: publicLatest.id }] };
  }, [publicLatest]);

  // Fase 2A · Consolidado multi-partido.
  const [consolidated, setConsolidated] = useState(null);
  const [consolidatedLoading, setConsolidatedLoading] = useState(false);
  useEffect(() => {
    const pid = data?.id;
    if (!pid) { setConsolidated(null); return undefined; }
    let alive = true;
    setConsolidatedLoading(true);
    fetchPlayerIntelligenceConsolidated(pid)
      .then((res) => { if (alive) setConsolidated(res || null); })
      .catch(() => { if (alive) setConsolidated(null); })
      .finally(() => { if (alive) setConsolidatedLoading(false); });
    return () => { alive = false; };
  }, [data?.id]);

  // Fase 1 Player Intelligence · Histórico multi-partido.
  // Reutiliza el endpoint existente `list_matches` sin crear uno nuevo.
  // La autorización server-side es la misma (PÚBLICO | PROPIETARIO | ADMIN
  // vía `authorize_player_profile_access`), por lo que no amplía permisos.
  const [matchesHistory, setMatchesHistory] = useState(null); // {items: []} | null
  const [matchesLoading, setMatchesLoading] = useState(false);
  useEffect(() => {
    const pid = data?.id;
    if (!pid) { setMatchesHistory(null); return undefined; }
    let alive = true;
    setMatchesLoading(true);
    fetchPlayerIntelligenceMatches(pid)
      .then((res) => { if (alive) setMatchesHistory(res || null); })
      .catch(() => { if (alive) setMatchesHistory(null); })
      .finally(() => { if (alive) setMatchesLoading(false); });
    return () => { alive = false; };
  }, [data?.id]);

  if (loading) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-10 w-48 mb-6" />
          <Skeleton className="h-80 w-full rounded-2xl" />
        </div>
      </MainLayout>
    );
  }

  if (error || !data) {
    return (
      <MainLayout>
        <div data-testid="player-public-error" className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <ShieldQuestion className="h-12 w-12 text-[var(--ns-text-muted)] mx-auto mb-4" />
          <h1 className="font-display text-2xl text-[var(--ns-text)] mb-2">Este perfil no está disponible para visualización pública.</h1>
          <p className="text-sm text-[var(--ns-text-muted)] mb-6">
            Puede que el club o el administrador no haya autorizado su consulta pública.
          </p>
          <Button asChild variant="outline" className="rounded-xl">
            <Link to={`/clubes/${clubSlug}/equipos/${teamSlug}`}>← Volver a la plantilla</Link>
          </Button>
        </div>
      </MainLayout>
    );
  }

  const p = data;
  const avatar = resolvePlayerAvatar({ profile_photo: p.profile_photo, gender: p.gender });
  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Link
            to={`/clubes/${clubSlug}/equipos/${teamSlug}`}
            className="inline-flex items-center gap-1 text-sm text-[var(--ns-text-muted)] hover:text-[var(--ns-gold-700)]"
          >
            <ArrowLeft className="h-4 w-4" /> Volver a la plantilla
          </Link>
        </div>

        <Card data-testid="player-public-card" className="p-6 sm:p-8 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-shrink-0">
              <div className="h-48 w-48 rounded-2xl overflow-hidden bg-[var(--ns-bg-2)] border border-[var(--ns-border)]">
                {avatar ? (
                  <img src={avatar} alt={p.full_name} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center">
                    <Trophy className="h-12 w-12 text-[var(--ns-text-subtle)]" />
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h1 data-testid="player-public-name" className="font-display text-2xl sm:text-3xl text-[var(--ns-text)]">{p.full_name}</h1>
              <p className="text-sm text-[var(--ns-text-muted)] mt-1">
                {[p.age != null ? `${p.age} años` : null, p.team_category, p.team_gender, p.position].filter(Boolean).join(' · ')}
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                {p.sport_name ? <Badge className="bg-[var(--ns-amber-50)] text-[var(--ns-gold-700)] uppercase text-[10px]">{p.sport_name}</Badge> : null}
                {p.club_name ? <Badge variant="outline" className="text-[10px]">{p.club_name}</Badge> : null}
                {p.team_name ? <Badge variant="outline" className="text-[10px]">{p.team_name}</Badge> : null}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5">
                <StatBox icon={Activity} label="Lado dominante" value={SIDE_LABEL[p.dominant_side] || '—'} testId="player-public-side" />
                <StatBox icon={Ruler} label="Altura" value={p.height_cm ? `${p.height_cm} cm` : '—'} testId="player-public-height" />
                <StatBox icon={Weight} label="Peso" value={p.weight_kg ? `${p.weight_kg} kg` : '—'} testId="player-public-weight" />
              </div>
              {p.bio ? (
                <article className="mt-6">
                  <h2 className="text-xs uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold mb-2">Biografía deportiva</h2>
                  <p data-testid="player-public-bio" className="text-sm text-[var(--ns-text)] leading-relaxed whitespace-pre-line">{p.bio}</p>
                </article>
              ) : null}
            </div>
          </div>
        </Card>

        <Card data-testid="player-public-highlights" className="p-5 sm:p-6 rounded-2xl mt-6 bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="h-4 w-4 text-[var(--ns-gold-600)]" />
            <h2 className="text-xs uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold">Highlights</h2>
            {Array.isArray(p.highlights) && p.highlights.length > 0 ? (
              <Badge className="bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)] text-[10px] uppercase">
                {p.highlights.length}
              </Badge>
            ) : null}
          </div>
          {Array.isArray(p.highlights) && p.highlights.length > 0 ? (
            <ul data-testid="player-public-highlights-list" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {p.highlights.map((h) => {
                const eventDate = h.event_date ? new Date(h.event_date).toLocaleDateString('es-ES', {
                  day: '2-digit', month: 'short', year: 'numeric',
                }) : null;
                const accessBadge = h.video_access_type === 'free'
                  ? { label: 'Gratis', cls: 'bg-emerald-100 text-emerald-800' }
                  : h.video_access_type === 'premium'
                  ? { label: 'Premium', cls: 'bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)]' }
                  : (h.video_access_type === 'pay_per_content' || h.video_access_type === 'premium_or_pay_per_content')
                  ? { label: 'Pago individual', cls: 'bg-sky-100 text-sky-800' }
                  : null;
                return (
                  <li
                    key={h.video_id}
                    data-testid={`player-public-highlight-${h.video_id}`}
                    className="flex flex-col rounded-xl bg-white border border-[var(--ns-border)] overflow-hidden"
                  >
                    <div className="relative aspect-video bg-[var(--ns-bg-2)]">
                      {h.video_thumbnail ? (
                        <img src={buildAssetUrl(h.video_thumbnail)} alt={h.video_title} className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-[var(--ns-text-subtle)]">
                          <Film className="h-8 w-8" />
                        </div>
                      )}
                      {accessBadge ? (
                        <span className={`absolute top-2 right-2 px-2 py-0.5 rounded-md text-[10px] uppercase font-semibold ${accessBadge.cls}`}>
                          {accessBadge.label}
                        </span>
                      ) : null}
                    </div>
                    <div className="p-3 flex-1 flex flex-col gap-1.5">
                      <p className="text-sm font-semibold text-[var(--ns-text)] truncate">{h.video_title}</p>
                      <p className="text-xs text-[var(--ns-text-muted)] truncate">
                        {h.event_name}{eventDate ? ` · ${eventDate}` : ''}
                      </p>
                      {h.action_type ? (
                        <p className="text-[11px] text-[var(--ns-text-subtle)]">
                          Acción: <span className="font-semibold text-[var(--ns-text)]">{h.action_type}</span>
                        </p>
                      ) : null}
                      {h.event_slug ? (
                        <Button
                          asChild
                          size="sm"
                          data-testid={`player-public-highlight-play-${h.video_id}`}
                          className="mt-2 h-8 rounded-lg bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] text-xs font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0"
                        >
                          <Link to={`/eventos/${h.event_slug}`} className="inline-flex items-center gap-1">
                            <PlayCircle className="h-3.5 w-3.5" /> Abrir en el partido
                          </Link>
                        </Button>
                      ) : null}
                      {/* Fase 06D · Descarga del contenido oficial (mismo video_id). Silencio si no aplica. */}
                      <VideoDownloadInline videoId={h.video_id} compact />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div data-testid="player-public-highlights-empty" className="p-6 rounded-xl border border-dashed border-[var(--ns-border-strong)] bg-[var(--ns-bg-2)]/40 text-center">
              <Film className="h-8 w-8 text-[var(--ns-text-subtle)] mx-auto mb-2" />
              <p className="text-sm text-[var(--ns-text-muted)]">
                Este jugador aún no tiene highlights oficiales publicados.
              </p>
            </div>
          )}
          <p className="text-[10px] text-[var(--ns-text-subtle)] mt-3">
            Al reproducir un highlight se abre el partido oficial en NetScouters. Se aplican las reglas de acceso del contenido (registro, Premium o compra individual).
          </p>
        </Card>

        {/*
          Sección "Rendimiento" — SIEMPRE visible en toda ficha de jugador,
          independientemente del rol o vinculación del visitante.

          Fase 1 Player Intelligence:
            • La sub-sección "Estadísticas" (placeholder original) se sustituye
              por el componente reutilizable `PlayerIntelligencePerformance`,
              que consume el contrato `{ player_matches: [...] }`.
            • El componente NO contiene datos de ningún jugador. Mientras no
              exista importador ni colección `player_match`, los datos vienen
              del fixture de desarrollo (`mario_player_intelligence.js`) sólo
              cuando la ficha abierta es la de Mario. Cualquier otro jugador
              recibe `player_matches: []` (estado A del componente).
            • La sub-sección "Análisis biomecánico" NO se modifica.
        */}
        {/*
          Fase 3A — Player Intelligence en la ficha pública. Se carga
          desde la API real usando el `player_profile_id` que ya devuelve
          el endpoint público (`data.id`). El servidor autoriza por
          PÚBLICO | PROPIETARIO | ADMIN antes de devolver el payload.
          Sin fallback runtime a fixtures.
        */}
        <PlayerIntelligencePerformance
          data={publicPlayerIntelligence}
        />

        {/* Fase 2A Player Intelligence — Rendimiento consolidado. */}
        <PlayerConsolidatedCard
          loading={consolidatedLoading}
          data={consolidated}
        />

        {/* Fase final multi-partido · Comparación dinámica partido a partido. */}
        <PlayerComparisonCard
          loading={matchesLoading}
          items={matchesHistory?.items || []}
          consolidated={consolidated}
        />

        {/* Fase final multi-partido · Evolución por partido (métrica seleccionable). */}
        <PlayerEvolutionCard
          loading={matchesLoading}
          items={matchesHistory?.items || []}
        />

        {/* Fase 1 Player Intelligence — Histórico de partidos. */}
        <PlayerMatchesHistoryCard
          playerProfileId={data?.id}
          loading={matchesLoading}
          items={matchesHistory?.items || []}
        />
        <Card
          data-testid="player-public-performance"
          className="p-5 sm:p-6 rounded-2xl mt-6 bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]"
        >
          <div className="flex items-center gap-2 mb-4">
            <Activity className="h-4 w-4 text-[var(--ns-gold-600)]" />
            <h2 className="text-xs uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold">
              Rendimiento
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <PerformanceOption
              icon={HeartPulse}
              title="Análisis biomecánico"
              description="Sección privada. Datos de salud y biomecánica del jugador."
              testId="player-public-performance-biomechanics"
            />
          </div>
          <p className="text-[10px] text-[var(--ns-text-subtle)] mt-3">
            El acceso al detalle se rige por los permisos y accesos ya definidos para cada tipo de contenido.
          </p>
        </Card>
      </div>
    </MainLayout>
  );
}

/**
 * Opción de rendimiento SIEMPRE visible. Muestra bloqueado/no disponible
 * cuando aún no hay datos, sin ocultar la tarjeta.
 */
function PerformanceOption({ icon: Icon, title, description, testId }) {
  return (
    <div
      data-testid={testId}
      className="relative flex flex-col gap-3 p-4 rounded-xl border border-dashed border-[var(--ns-border-strong)] bg-[var(--ns-amber-50)]/30"
    >
      <div className="flex items-center justify-between">
        <div className="h-10 w-10 rounded-xl bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] inline-flex items-center justify-center">
          <Icon className="h-5 w-5" />
        </div>
        <Badge
          className="bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)] text-[10px] uppercase inline-flex items-center gap-1"
          data-testid={`${testId}-locked`}
        >
          <Lock className="h-3 w-3" /> No disponible
        </Badge>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-[var(--ns-text)] uppercase tracking-wide">
          {title}
        </h3>
        <p className="text-xs text-[var(--ns-text-muted)] mt-1 leading-relaxed">
          {description}
        </p>
      </div>
      <p className="text-[11px] text-[var(--ns-text-subtle)]">
        Sin datos disponibles todavía.
      </p>
    </div>
  );
}

function StatBox({ icon: Icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="p-3 rounded-xl border border-[var(--ns-border)] bg-white">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <p className="text-sm font-semibold text-[var(--ns-text)] mt-1">{value}</p>
    </div>
  );
}

/**
 * Fase 1 Player Intelligence — Histórico de partidos.
 *
 * Reutiliza `GET /api/player-intelligence/players/{id}/matches` (list_matches).
 * No crea endpoints ni duplica la vista de análisis individual. Cada fila
 * enlaza a `/jugadores/:playerProfileId/analisis/:matchId` existente.
 *
 * Reglas:
 *   - Orden: más reciente → más antiguo (ya resuelto en backend por
 *     `_sort_key = (match_date, created_at) DESC`).
 *   - Campos ausentes → `—` neutro, sin romper la tarjeta ni inventar
 *     datos.
 *   - Deduplicación: el backend garantiza 1 doc por
 *     `(player_profile_id, source_match_id)` en importación. El listado
 *     ya es 1:1 con partidos lógicos.
 */
function PlayerMatchesHistoryCard({ playerProfileId, loading, items }) {
  const list = Array.isArray(items) ? items : [];
  const dash = '—';
  const fmtDate = (iso) => {
    if (!iso) return dash;
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleDateString('es-ES', {
        day: '2-digit', month: '2-digit', year: 'numeric',
      });
    } catch (_) { return iso; }
  };
  return (
    <Card
      data-testid="player-public-matches-history"
      className="p-5 sm:p-6 rounded-2xl mt-6 bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]"
    >
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-[var(--ns-gold-600)]" />
          <h2 className="text-xs uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold">
            Histórico de partidos
          </h2>
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-[var(--ns-text-muted)]" />}
      </div>

      {(!loading && list.length === 0) && (
        <p
          className="text-xs text-[var(--ns-text-subtle)]"
          data-testid="player-public-matches-history-empty"
        >
          Todavía no hay partidos analizados para este jugador.
        </p>
      )}

      {list.length > 0 && (
        <ul className="divide-y divide-[var(--ns-border)]" data-testid="player-public-matches-history-list">
          {list.map((m) => (
            <li
              key={m.id}
              className="py-3 flex flex-col sm:flex-row sm:items-center sm:gap-4"
              data-testid={`player-public-matches-history-item-${m.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-[var(--ns-text)]">
                    {fmtDate(m.match_date)}
                  </span>
                  <span className="text-sm text-[var(--ns-text)]">·</span>
                  <span className="text-sm text-[var(--ns-text)] truncate">
                    vs {m.opponent || dash}
                  </span>
                  {m.result ? (
                    <Badge className="bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] border border-[var(--ns-gold)]/30">
                      {m.result}
                    </Badge>
                  ) : null}
                  {m.data_quality ? (
                    <Badge variant="outline" className="text-[10px] uppercase">
                      {m.data_quality}
                    </Badge>
                  ) : null}
                </div>
                <div className="mt-1 text-xs text-[var(--ns-text-muted)] flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>Posición: <span className="text-[var(--ns-text)]">{m.position || dash}</span></span>
                  <span>Minutos: <span className="text-[var(--ns-text)]">{(m.minutes ?? null) !== null ? m.minutes : dash}</span></span>
                </div>
              </div>
              <div className="mt-2 sm:mt-0 flex-shrink-0">
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-[var(--ns-border-strong)] text-[var(--ns-text)] hover:bg-[var(--ns-amber-50)] hover:border-[var(--ns-gold)]"
                  data-testid={`player-public-matches-history-cta-${m.id}`}
                >
                  <Link to={`/jugadores/${encodeURIComponent(playerProfileId)}/analisis/${encodeURIComponent(m.id)}`}>
                    Ver análisis <ChevronRight className="h-3.5 w-3.5 ml-1" />
                  </Link>
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * Fase 2A · Rendimiento consolidado multi-partido.
 * Reutiliza `GET /api/player-intelligence/players/{id}/consolidated`.
 * Redondeo visual sólo en frontend. Si matches_total<=0, oculta.
 *
 * Fase final multi-partido: amplía los bloques visibles usando TODAS
 * las métricas consolidables realmente disponibles, agrupadas por área
 * (Pase, Defensa, Duelos, Finalización, Participación, Por 90) con la
 * cobertura "Datos: X/Y partidos" junto a cada valor.
 */
function PlayerConsolidatedCard({ loading, data }) {
  if (!data || (data.matches_total ?? 0) <= 0) return null;
  const dash = '—';
  const r1 = (v) => (v == null ? dash : (Math.round(v * 10) / 10).toLocaleString('es-ES'));
  const int0 = (v) => (v == null ? dash : Math.round(v).toLocaleString('es-ES'));

  const counts = data.counts || {};
  const ratios = data.ratios || {};
  const per90 = data.per_90 || {};

  const positionStr = Object.entries(data.position_counts || {})
    .sort((a, b) => b[1] - a[1])
    .map(([p, n]) => `${p} (${n})`)
    .join(' · ');

  // Celda consolidada estándar.
  const Cell = ({ label, main, sub, testid }) => (
    <div className="p-3 rounded-xl border border-[var(--ns-border)] bg-white" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--ns-text-muted)]">{label}</div>
      <div className="text-xl font-semibold text-[var(--ns-text)] mt-1">{main}</div>
      {sub ? <div className="text-xs text-[var(--ns-text-muted)] mt-0.5">{sub}</div> : null}
    </div>
  );

  // Helpers: sólo renderiza si matches_used>=1 y value no es null.
  const coverage = (used, total) => (used != null && total != null) ? `Datos: ${used}/${total} partidos` : null;
  const countCell = (key, label, testid, { asInt = true } = {}) => {
    const c = counts[key];
    if (!c || c.value == null || c.matches_used < 1) return null;
    return (
      <Cell
        key={key}
        label={label}
        main={asInt ? int0(c.value) : r1(c.value)}
        sub={coverage(c.matches_used, c.matches_total)}
        testid={testid}
      />
    );
  };
  const ratioCell = (key, label, testid) => {
    const r = ratios[key];
    if (!r || r.value == null) return null;
    return (
      <Cell
        key={key}
        label={label}
        main={`${r1(r.value)} %`}
        sub={`${int0(r.numerator)} / ${int0(r.denominator)} · ${coverage(r.matches_used, r.matches_total) || ''}`.trim()}
        testid={testid}
      />
    );
  };
  const per90Cell = (key, label, testid) => {
    const p = per90[key];
    if (!p || p.value == null) return null;
    return (
      <Cell
        key={key}
        label={label}
        main={r1(p.value)}
        sub={`${int0(p.numerator)} en ${int0(p.denominator_minutes)}' · ${coverage(p.matches_used, p.matches_total) || ''}`.trim()}
        testid={testid}
      />
    );
  };

  // Secciones. Cada sección renderiza sus celdas sólo si existe algo.
  const secPase = [
    countCell('passing.attempted', 'Pases intentados', 'consolidated-passing-attempted'),
    countCell('passing.completed', 'Pases completados', 'consolidated-passing-completed'),
    countCell('passing.failed', 'Pases fallados', 'consolidated-passing-failed'),
    ratioCell('passing.accuracy_pct', 'Precisión de pase', 'consolidated-passing-accuracy'),
    countCell('passing.forward', 'Pases hacia adelante', 'consolidated-passing-forward'),
    countCell('passing.key_passes', 'Pases clave', 'consolidated-passing-keypasses'),
    countCell('passing.assists', 'Asistencias', 'consolidated-passing-assists'),
  ].filter(Boolean);
  const secDef = [
    countCell('defending.recoveries', 'Recuperaciones', 'consolidated-def-recoveries'),
    countCell('defending.interceptions', 'Interceptaciones', 'consolidated-def-interceptions'),
    countCell('defending.clearances', 'Despejes', 'consolidated-def-clearances'),
    countCell('defending.blocks', 'Bloqueos', 'consolidated-def-blocks'),
    countCell('defending.pressures', 'Presiones', 'consolidated-def-pressures'),
    countCell('defending.fouls_committed', 'Faltas', 'consolidated-def-fouls'),
  ].filter(Boolean);
  const secDuels = [
    countCell('duels.total_events', 'Duelos totales', 'consolidated-duels-total'),
    countCell('duels.won', 'Duelos ganados', 'consolidated-duels-won'),
    countCell('duels.lost', 'Duelos perdidos', 'consolidated-duels-lost'),
    ratioCell('duels.win_pct', '% duelos ganados', 'consolidated-duels-winpct'),
  ].filter(Boolean);
  const secFin = [
    countCell('finishing_regulation.shots', 'Tiros', 'consolidated-fin-shots'),
    countCell('finishing_regulation.on_target', 'Tiros a puerta', 'consolidated-fin-ontarget'),
    countCell('finishing_regulation.goals', 'Goles', 'consolidated-fin-goals'),
    ratioCell('finishing_regulation.conversion_pct', 'Conversión', 'consolidated-fin-conv'),
    countCell('finishing_regulation.chances_created', 'Ocasiones creadas', 'consolidated-fin-chances'),
  ].filter(Boolean);
  const secPart = [
    countCell('receiving.total', 'Recepciones', 'consolidated-rec-total'),
    countCell('crossing.attempted', 'Centros intentados', 'consolidated-cross-att'),
    countCell('crossing.completed', 'Centros completados', 'consolidated-cross-ok'),
    ratioCell('crossing.accuracy_pct', 'Precisión de centros', 'consolidated-cross-acc'),
  ].filter(Boolean);
  const secPer90 = [
    per90Cell('defending.recoveries_per_90', 'Recuperaciones /90', 'consolidated-rec-per90'),
    per90Cell('passing.completed_per_90', 'Pases completados /90', 'consolidated-passes-per90'),
  ].filter(Boolean);

  const Block = ({ title, cells }) => (
    cells.length === 0 ? null : (
      <div className="mt-4 first:mt-0" data-testid={`consolidated-block-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <div className="text-[11px] uppercase tracking-widest text-[var(--ns-text-subtle)] font-semibold mb-2">{title}</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">{cells}</div>
      </div>
    )
  );

  return (
    <Card
      data-testid="player-public-consolidated"
      className="p-5 sm:p-6 rounded-2xl mt-6 bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]"
    >
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Gauge className="h-4 w-4 text-[var(--ns-gold-600)]" />
          <h2 className="text-xs uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold">
            Rendimiento consolidado
          </h2>
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-[var(--ns-text-muted)]" />}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Badge className="bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] border border-[var(--ns-gold)]/30">
          {data.matches_total} partidos analizados
        </Badge>
        <Badge variant="outline">{int0(data.total_minutes)} minutos</Badge>
        {positionStr ? <Badge variant="outline">Posición: {positionStr}</Badge> : null}
        {data.date_first_match && data.date_last_match ? (
          <span className="text-[11px] text-[var(--ns-text-muted)]">
            {new Date(data.date_first_match).toLocaleDateString('es-ES')} — {new Date(data.date_last_match).toLocaleDateString('es-ES')}
          </span>
        ) : null}
      </div>

      <Block title="Pase" cells={secPase} />
      <Block title="Defensa" cells={secDef} />
      <Block title="Duelos" cells={secDuels} />
      <Block title="Finalización" cells={secFin} />
      <Block title="Participación" cells={secPart} />
      <Block title="Por 90" cells={secPer90} />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Fase final multi-partido · Catálogo compartido de métricas comparables.
// Define etiquetas y formato por métrica. Sólo se muestran las métricas con
// datos reales en al menos un partido (Comparación) o >=2 partidos
// (Evolución). No se recalcula nada: los valores vienen de
// `items[i].metrics` (backend `_light_item.metrics`).
// ---------------------------------------------------------------------------
const COMPARISON_METRICS = [
  // Pase
  { key: 'passing.attempted', label: 'Pases intentados', fmt: 'int' },
  { key: 'passing.completed', label: 'Pases completados', fmt: 'int' },
  { key: 'passing.accuracy_pct', label: 'Precisión de pase', fmt: 'pct' },
  { key: 'passing.forward', label: 'Pases hacia adelante', fmt: 'int' },
  { key: 'passing.key_passes', label: 'Pases clave', fmt: 'int' },
  { key: 'passing.assists', label: 'Asistencias', fmt: 'int' },
  // Defensa
  { key: 'defending.recoveries', label: 'Recuperaciones', fmt: 'int' },
  { key: 'defending.interceptions', label: 'Interceptaciones', fmt: 'int' },
  { key: 'defending.clearances', label: 'Despejes', fmt: 'int' },
  { key: 'defending.blocks', label: 'Bloqueos', fmt: 'int' },
  { key: 'defending.pressures', label: 'Presiones', fmt: 'int' },
  // Duelos
  { key: 'duels.total_events', label: 'Duelos totales', fmt: 'int' },
  { key: 'duels.won', label: 'Duelos ganados', fmt: 'int' },
  { key: 'duels.lost', label: 'Duelos perdidos', fmt: 'int' },
  { key: 'duels.win_pct', label: '% duelos ganados', fmt: 'pct' },
  // Finalización
  { key: 'finishing_regulation.shots', label: 'Tiros', fmt: 'int' },
  { key: 'finishing_regulation.on_target', label: 'Tiros a puerta', fmt: 'int' },
  { key: 'finishing_regulation.goals', label: 'Goles', fmt: 'int' },
  { key: 'finishing_regulation.conversion_pct', label: 'Conversión', fmt: 'pct' },
  // Participación
  { key: 'receiving.total', label: 'Recepciones', fmt: 'int' },
  { key: 'crossing.attempted', label: 'Centros intentados', fmt: 'int' },
  { key: 'crossing.completed', label: 'Centros completados', fmt: 'int' },
  { key: 'crossing.accuracy_pct', label: 'Precisión de centros', fmt: 'pct' },
  // Por 90
  { key: 'defending.recoveries_per_90', label: 'Recuperaciones /90', fmt: 'num1' },
  { key: 'passing.completed_per_90', label: 'Pases completados /90', fmt: 'num1' },
];

function _fmtMetricValue(v, fmt) {
  if (v == null || Number.isNaN(v)) return '—';
  if (fmt === 'int') return Math.round(v).toLocaleString('es-ES');
  if (fmt === 'pct') return `${(Math.round(v * 10) / 10).toLocaleString('es-ES')} %`;
  if (fmt === 'num1') return (Math.round(v * 10) / 10).toLocaleString('es-ES');
  return String(v);
}

function _consolidatedValueFor(data, key) {
  if (!data) return null;
  const r = (data.ratios || {})[key];
  if (r && r.value != null) return r.value;
  const p = (data.per_90 || {})[key];
  if (p && p.value != null) return p.value;
  const c = (data.counts || {})[key];
  if (c && c.value != null) return c.value;
  return null;
}

/**
 * Fase final multi-partido · Comparación dinámica partido a partido.
 *
 * - Filas: métricas realmente presentes en >=1 partido (del catálogo
 *   `COMPARISON_METRICS`).
 * - Columnas: una por partido (orden cronológico ASC por match_date) + una
 *   columna final "Consolidado" con el valor del endpoint.
 * - Métricas ausentes → "—" (nunca 0 artificial).
 * - No interpreta mejora/empeora; es puramente objetiva.
 * - Reutiliza `items[i].metrics` expuesto por `list_matches`; sin endpoints
 *   nuevos, sin recálculos en frontend.
 */
function PlayerComparisonCard({ loading, items, consolidated }) {
  const list = Array.isArray(items) ? items.filter((it) => it && it.metrics) : [];
  if (!loading && list.length < 1) return null;
  // Orden cronológico ASC por match_date real (no por importación).
  const matches = [...list].sort((a, b) => {
    const da = a.match_date || '';
    const db = b.match_date || '';
    if (da === db) return 0;
    return da > db ? 1 : -1;
  });
  // Sólo métricas con al menos un valor no-null en algún partido.
  const visibleRows = COMPARISON_METRICS.filter((row) =>
    matches.some((m) => m.metrics && m.metrics[row.key] != null)
  );
  if (visibleRows.length === 0) return null;

  const fmtDateShort = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
    } catch (_) { return iso; }
  };

  return (
    <Card
      data-testid="player-public-comparison"
      className="p-5 sm:p-6 rounded-2xl mt-6 bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]"
    >
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Columns className="h-4 w-4 text-[var(--ns-gold-600)]" />
          <h2 className="text-xs uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold">
            Comparación de partidos
          </h2>
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-[var(--ns-text-muted)]" />}
      </div>
      <div className="overflow-x-auto" data-testid="player-public-comparison-scroll">
        <table className="w-full min-w-[520px] text-sm" data-testid="player-public-comparison-table">
          <thead>
            <tr className="text-left text-[var(--ns-text-muted)]">
              <th className="py-2 pr-3 text-[10px] uppercase tracking-widest font-semibold">Métrica</th>
              {matches.map((m, idx) => (
                <th
                  key={m.id}
                  className="py-2 px-3 text-[10px] uppercase tracking-widest font-semibold whitespace-nowrap"
                  data-testid={`comparison-col-${idx + 1}`}
                  title={`${m.opponent || ''} · ${m.match_date || ''}`}
                >
                  P{idx + 1} · {fmtDateShort(m.match_date)}
                  <div className="text-[9px] normal-case font-normal text-[var(--ns-text-subtle)] truncate max-w-[120px]">
                    vs {m.opponent || '—'}
                  </div>
                </th>
              ))}
              <th
                className="py-2 pl-3 text-[10px] uppercase tracking-widest font-semibold whitespace-nowrap bg-[var(--ns-amber-50)]/40"
                data-testid="comparison-col-consolidated"
              >
                Consolidado
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr
                key={row.key}
                className="border-t border-[var(--ns-border)]"
                data-testid={`comparison-row-${row.key.replace(/\./g, '-')}`}
              >
                <td className="py-2 pr-3 text-[var(--ns-text)]">{row.label}</td>
                {matches.map((m, idx) => {
                  const v = m.metrics?.[row.key];
                  return (
                    <td
                      key={m.id}
                      className="py-2 px-3 text-[var(--ns-text)] whitespace-nowrap"
                      data-testid={`comparison-cell-${row.key.replace(/\./g, '-')}-p${idx + 1}`}
                    >
                      {_fmtMetricValue(v, row.fmt)}
                    </td>
                  );
                })}
                <td
                  className="py-2 pl-3 text-[var(--ns-text)] font-semibold whitespace-nowrap bg-[var(--ns-amber-50)]/40"
                  data-testid={`comparison-cell-${row.key.replace(/\./g, '-')}-consolidated`}
                >
                  {_fmtMetricValue(_consolidatedValueFor(consolidated, row.key), row.fmt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[11px] text-[var(--ns-text-subtle)]">
        Ordenado cronológicamente por fecha del partido. Métricas ausentes se muestran como "—".
      </p>
    </Card>
  );
}

/**
 * Fase final multi-partido · Evolución por partido (gráfica dinámica).
 *
 * - Eje X: orden cronológico ASC por match_date.
 * - Eje Y: valor real de la métrica seleccionada.
 * - Selector construido sólo con métricas disponibles en ≥2 partidos
 *   (para que la gráfica tenga sentido evolutivo).
 * - Partidos que no aportan la métrica seleccionada se omiten del punto
 *   (no se interpolan ni se convierten a 0).
 * - Reutiliza `items[i].metrics` expuesto por `list_matches`; sin
 *   recálculos. No muestra interpretaciones (mejora/empeora/scoring).
 */
function PlayerEvolutionCard({ loading, items }) {
  const list = Array.isArray(items) ? items.filter((it) => it && it.metrics) : [];
  const matches = React.useMemo(() => {
    return [...list].sort((a, b) => {
      const da = a.match_date || '';
      const db = b.match_date || '';
      if (da === db) return 0;
      return da > db ? 1 : -1;
    });
  }, [list]);

  // Sólo métricas con valor real en ≥2 partidos.
  const availableMetrics = React.useMemo(
    () => COMPARISON_METRICS.filter((row) => {
      let n = 0;
      for (const m of matches) {
        if (m.metrics && m.metrics[row.key] != null) n += 1;
        if (n >= 2) return true;
      }
      return false;
    }),
    [matches],
  );

  const [selected, setSelected] = useState(null);
  useEffect(() => {
    if (availableMetrics.length > 0) {
      setSelected((prev) => (prev && availableMetrics.some((m) => m.key === prev) ? prev : availableMetrics[0].key));
    } else {
      setSelected(null);
    }
  }, [availableMetrics]);

  if (!loading && (matches.length < 2 || availableMetrics.length === 0)) return null;

  const currentMetric = availableMetrics.find((m) => m.key === selected) || availableMetrics[0];
  const chartData = (currentMetric
    ? matches
        .filter((m) => m.metrics?.[currentMetric.key] != null)
        .map((m) => ({
          match_id: m.id,
          match_date: m.match_date,
          opponent: m.opponent || '—',
          minutes: m.minutes,
          value: (() => {
            const raw = m.metrics[currentMetric.key];
            if (currentMetric.fmt === 'num1' || currentMetric.fmt === 'pct') {
              return Math.round(raw * 10) / 10;
            }
            return raw;
          })(),
        }))
    : []);

  const fmtDateShort = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
    } catch (_) { return iso; }
  };

  return (
    <Card
      data-testid="player-public-evolution"
      className="p-5 sm:p-6 rounded-2xl mt-6 bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <LineChartIcon className="h-4 w-4 text-[var(--ns-gold-600)]" />
          <h2 className="text-xs uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold">
            Evolución por partido
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {loading && <Loader2 className="h-4 w-4 animate-spin text-[var(--ns-text-muted)]" />}
          {availableMetrics.length > 0 && (
            <select
              value={selected || ''}
              onChange={(e) => setSelected(e.target.value)}
              className="text-xs rounded-lg border border-[var(--ns-border-strong)] bg-white px-2 py-1 text-[var(--ns-text)] focus:outline-none focus:ring-2 focus:ring-[var(--ns-gold)]/40"
              data-testid="evolution-metric-select"
              aria-label="Métrica"
            >
              {availableMetrics.map((m) => (
                <option key={m.key} value={m.key}>{m.label}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {chartData.length < 2 ? (
        <p
          className="text-xs text-[var(--ns-text-subtle)]"
          data-testid="player-public-evolution-empty"
        >
          Necesita al menos 2 partidos con la métrica seleccionada para mostrar la evolución.
        </p>
      ) : (
        <div className="w-full h-64" data-testid="player-public-evolution-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--ns-border)" vertical={false} />
              <XAxis
                dataKey="match_date"
                tickFormatter={fmtDateShort}
                tick={{ fontSize: 11, fill: 'var(--ns-text-muted)' }}
                stroke="var(--ns-border-strong)"
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'var(--ns-text-muted)' }}
                stroke="var(--ns-border-strong)"
                width={48}
              />
              <RTooltip
                contentStyle={{
                  background: 'white',
                  border: '1px solid var(--ns-border-strong)',
                  borderRadius: 10,
                  fontSize: 12,
                }}
                formatter={(val) => [_fmtMetricValue(val, currentMetric.fmt), currentMetric.label]}
                labelFormatter={(_, payload) => {
                  const p = payload && payload[0] && payload[0].payload;
                  if (!p) return '';
                  const dateStr = p.match_date ? new Date(p.match_date).toLocaleDateString('es-ES') : '—';
                  const minStr = p.minutes != null ? ` · ${p.minutes}'` : '';
                  return `${dateStr} · vs ${p.opponent}${minStr}`;
                }}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke="var(--ns-gold-600)"
                strokeWidth={2}
                dot={{ r: 4, fill: 'var(--ns-gold-600)', stroke: 'white', strokeWidth: 1 }}
                activeDot={{ r: 6 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
